import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { inviteBackofficeUserSchema, type ClubUserDto, type RoleDto } from "@tc/shared";
import { Button, Card, Field, Icon, Modal, Select, TextInput, Toast } from "@/components/ui";
import { useAuthz } from "@/lib/auth/authz";
import { useFormatters } from "@/lib/i18n/format";
import {
  inviteBackofficeUser,
  listBackofficeRoles,
  listBackofficeTeam,
  resendBackofficeInvite,
  updateBackofficeUser,
  TeamError,
} from "../api";

/**
 * Settings → Team — back-office team management. The org-scoped sibling of the club TeamSection:
 * a TC admin lists all back-office users, invites new ones (name + email + role; the server emails
 * a set-password link), changes a teammate's role, and removes (disables) or restores them.
 * Mounted only for back-office users holding `user.manage` (see SettingsPage). Unlike the club
 * portal, roles are user-facing here — Admin / Staff / Read-Only differ meaningfully.
 */
export function BackOfficeTeamSection() {
  const { t } = useTranslation();
  const { me } = useAuthz();

  const [users, setUsers] = useState<ClubUserDto[]>([]);
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [team, assignable] = await Promise.all([listBackofficeTeam(), listBackofficeRoles()]);
      setUsers(team);
      setRoles(assignable);
    } catch {
      /* surfaced via the empty state */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function flash(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(null), 3500);
  }

  async function setStatus(user: ClubUserDto, status: ClubUserDto["status"]) {
    const updated = await updateBackofficeUser(user.id, { status });
    setUsers((prev) => prev.map((u) => (u.id === user.id ? updated : u)));
  }

  async function setRole(user: ClubUserDto, roleId: string) {
    const updated = await updateBackofficeUser(user.id, { roleId });
    setUsers((prev) => prev.map((u) => (u.id === user.id ? updated : u)));
  }

  async function resend(user: ClubUserDto) {
    await resendBackofficeInvite(user.id);
    flash(t("settings.boTeam.resent", { email: user.email }));
  }

  return (
    <div>
      <div className="set-section-head row-between" style={{ marginBottom: 18 }}>
        <div>
          <h2>{t("settings.boTeam.heading")}</h2>
          <div className="sub">{t("settings.boTeam.sub")}</div>
        </div>
        <Button icon="plus" onClick={() => setInviteOpen(true)}>
          {t("settings.boTeam.invite")}
        </Button>
      </div>

      {loading ? (
        <div className="empty">{t("common.comingSoon")}</div>
      ) : users.length === 0 ? (
        <div className="empty">
          <div className="empty-ic">
            <Icon name="roster" size={24} />
          </div>
          {t("settings.boTeam.empty")}
        </div>
      ) : (
        <Card>
          <table className="table">
            <thead>
              <tr>
                <th>{t("settings.team.name")}</th>
                <th>{t("settings.boTeam.role")}</th>
                <th>{t("settings.team.status")}</th>
                <th>{t("settings.team.lastLogin")}</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <TeamRow
                  key={user.id}
                  user={user}
                  roles={roles}
                  isSelf={user.id === me?.id}
                  onSetStatus={setStatus}
                  onSetRole={setRole}
                  onResend={resend}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {inviteOpen && (
        <InviteModal
          roles={roles}
          onClose={() => setInviteOpen(false)}
          onInvited={async (email) => {
            setInviteOpen(false);
            await load();
            flash(t("settings.team.inviteSent", { email }));
          }}
        />
      )}

      {toast && <Toast message={toast} />}
    </div>
  );
}

interface TeamRowProps {
  user: ClubUserDto;
  roles: RoleDto[];
  isSelf: boolean;
  onSetStatus: (user: ClubUserDto, status: ClubUserDto["status"]) => Promise<void>;
  onSetRole: (user: ClubUserDto, roleId: string) => Promise<void>;
  onResend: (user: ClubUserDto) => Promise<void>;
}

function TeamRow({ user, roles, isSelf, onSetStatus, onSetRole, onResend }: TeamRowProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const [busy, setBusy] = useState(false);

  async function guard(fn: () => Promise<void>) {
    setBusy(true);
    try {
      await fn();
    } catch {
      /* keep prior state; a follow-up action can retry */
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <td className="t-trip">
        <b>
          {user.name}
          {isSelf && <span className="tag" style={{ marginLeft: 8 }}>{t("settings.team.you")}</span>}
        </b>
        <small>{user.email}</small>
      </td>
      <td>
        {/* You can't change your own role (also enforced server-side) — shown read-only. */}
        {isSelf ? (
          (user.roles[0]?.name ?? "—")
        ) : (
          <Select
            value={user.roles[0]?.id ?? ""}
            options={roles.map((r) => ({ value: r.id, label: r.name }))}
            onChange={(roleId) => void guard(() => onSetRole(user, roleId))}
          />
        )}
      </td>
      <td>
        <div className="admin-status-cell">
          <TeamStatusPill status={user.status} />
          {user.pending && <span className="tag">{t("settings.team.pending")}</span>}
        </div>
      </td>
      <td>{user.lastLoginAt ? fmt.date(user.lastLoginAt) : t("settings.team.never")}</td>
      <td>
        <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
          {user.pending && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => void guard(() => onResend(user))}>
              {t("settings.boTeam.resend")}
            </Button>
          )}
          {/* A teammate can never remove themselves (also enforced server-side). */}
          {!isSelf &&
            (user.status === "active" ? (
              <Button variant="soft" size="sm" disabled={busy} onClick={() => void guard(() => onSetStatus(user, "disabled"))}>
                {t("settings.team.remove")}
              </Button>
            ) : (
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => void guard(() => onSetStatus(user, "active"))}>
                {t("settings.team.restore")}
              </Button>
            ))}
        </div>
      </td>
    </tr>
  );
}

function TeamStatusPill({ status }: { status: ClubUserDto["status"] }) {
  const { t } = useTranslation();
  const ok = status === "active";
  const style = ok
    ? { color: "var(--success)", background: "var(--success-soft)" }
    : { color: "var(--danger)", background: "var(--danger-soft)" };
  return (
    <span className="badge" style={style}>
      <span className="badge-dot" style={{ background: style.color }} />
      {t(ok ? "settings.team.statusActive" : "settings.team.statusRemoved")}
    </span>
  );
}

function InviteModal({
  roles,
  onClose,
  onInvited,
}: {
  roles: RoleDto[];
  onClose: () => void;
  onInvited: (email: string) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState(
    () => (roles.find((r) => r.name === "Back Office Admin") ?? roles[0])?.id ?? "",
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = inviteBackofficeUserSchema.safeParse({ firstName, lastName, email, roleId });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("settings.team.saveError"));
      return;
    }
    setSaving(true);
    try {
      await inviteBackofficeUser(parsed.data);
      await onInvited(email);
    } catch (err) {
      setError(err instanceof TeamError ? err.message : t("settings.team.saveError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={t("settings.boTeam.inviteTitle")}>
      <form onSubmit={onSubmit}>
        <div className="modal-body">
          {error && (
            <div className="auth-alert" style={{ marginBottom: 16 }} role="alert">
              <Icon name="alert" size={16} className="aa-ic" />
              <div className="aa-body">{error}</div>
            </div>
          )}
          <div className="admin-form">
            <Field label={t("settings.team.firstName")} required>
              <TextInput icon="user" value={firstName} onChange={setFirstName} />
            </Field>
            <Field label={t("settings.team.lastName")} required>
              <TextInput icon="user" value={lastName} onChange={setLastName} />
            </Field>
            <Field label={t("settings.team.email")} required>
              <TextInput icon="mail" type="email" value={email} onChange={setEmail} />
            </Field>
            <Field label={t("settings.boTeam.role")} required>
              <Select
                value={roleId}
                options={roles.map((r) => ({ value: r.id, label: r.name }))}
                onChange={setRoleId}
              />
            </Field>
          </div>
          <p className="faint" style={{ fontSize: 12, marginTop: 12 }}>
            {t("settings.team.inviteHint")}
          </p>
        </div>
        <div className="modal-foot">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("actions.cancel")}
          </Button>
          <Button type="submit" disabled={saving}>
            {t("settings.team.sendInvite")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
